/**
 * Tier 1 — the 8 static interview questions.
 *
 * Copied verbatim from the prototype's `QS` array (IdeaLab.dc.html). The
 * handoff treats this copy as final: do not paraphrase question text, hints, or
 * placeholders.
 *
 * WHY EXACTLY 8, AND WHY STATIC:
 * Identical questions for every user are what make cohort percentiles
 * comparable at all — a per-user question set would score people against
 * different instruments. There is no LLM call in this tier and no dynamic
 * probing (Journey spec §06).
 *
 * The design handoff's README describes 11 questions plus a conditional probe,
 * and screenshots/02 shows "QUESTION 1 OF 11". Both are stale: the prototype's
 * live `QS` array has 8 entries with no probe logic, its screen set no longer
 * matches those screenshots, and the README itself notes the screenshots
 * "predate the dashboard iteration". See BACKLOG.md §0.1.
 */

/** The 7 coverage areas driving the progress segments. */
export const INTERVIEW_AREAS = [
  { n: 'Problem & who has it', short: 'Problem' },
  { n: 'Why now', short: 'Why now' },
  { n: 'Your unique insight', short: 'Insight' },
  { n: 'Alternatives & competition', short: 'Competition' },
  { n: 'Your edge', short: 'Edge' },
  { n: 'Business model', short: 'Model' },
  { n: 'Why you', short: 'Why you' },
] as const;

export interface InterviewQuestion {
  id: InterviewQuestionId;
  /** Index into INTERVIEW_AREAS. Two questions share area 0. */
  area: number;
  /** Question text. Shipping copy — do not reword. */
  q: string;
  /** Sub-hint under the question. */
  hint: string;
  /** Textarea placeholder. */
  ph: string;
  /**
   * Quick-reply chips. These SEED THE TEXTAREA — they never auto-submit
   * (Journey spec §06). The README claims clicking a chip submits the answer;
   * that is the stale draft.
   */
  chips?: readonly string[];
  /** Shown during the thinking beat after this answer, when present. */
  ack?: string;
}

export type InterviewQuestionId =
  | 'oneliner'
  | 'who'
  | 'whynow'
  | 'insight'
  | 'alts'
  | 'edge'
  | 'model'
  | 'whyyou';

export const INTERVIEW_QUESTIONS: readonly InterviewQuestion[] = [
  {
    id: 'oneliner',
    area: 0,
    q: 'In one sentence, what are you building?',
    hint: "Plain language beats pitch language. If you'd say it differently to a friend, say it that way.",
    ph: 'A meal planner that works around rotating shift schedules…',
  },
  {
    id: 'who',
    area: 0,
    q: 'Who feels this problem most acutely?',
    hint: "One specific group beats a category — we'll test the edges in the research. A rough guess is fine.",
    ack: "A specific group — that's exactly what the scan needs…",
    chips: ['Consumers', 'Businesses', 'Both'],
    ph: 'Night-shift nurses, warehouse crews…',
  },
  {
    id: 'whynow',
    area: 1,
    q: 'What changed in the last two years that makes this possible now?',
    hint: 'New technology, new regulation, a new behaviour, or a cost that finally dropped.',
    ph: "Something that wasn't true in 2023…",
  },
  {
    id: 'insight',
    area: 2,
    q: 'What do you believe about this problem that most people in the space get wrong?',
    hint: "Most founders find this one hardest — rough and honest beats polished. Skipping is fine; we'll dig for it in research.",
    ack: "That's the part research can't replace…",
    ph: 'Most people assume… but actually…',
  },
  {
    id: 'alts',
    area: 3,
    q: 'What do people use instead today?',
    hint: '“Nothing” is a real answer — and often the most interesting one.',
    chips: [
      'Spreadsheets',
      'A direct competitor',
      'Manual workarounds',
      'Nothing at all',
      'Not sure',
    ],
    ph: "What they'd have to give up to switch to you…",
  },
  {
    id: 'edge',
    area: 4,
    q: 'If a well-funded team copied you next quarter, why would you still win?',
    hint: 'Distribution, proprietary data, sequencing, or simply being early all count.',
    ack: 'Interesting moat — the research will stress-test it…',
    ph: 'The thing that gets harder to copy over time…',
  },
  {
    id: 'model',
    area: 5,
    q: 'How would you make money?',
    hint: 'An honest “not sure yet” is better than a number you don\'t believe.',
    chips: [
      'Subscription',
      'Usage-based',
      'Marketplace fee',
      'One-time purchase',
      'Not sure yet',
    ],
    ph: 'Who pays, how often, for what…',
  },
  {
    id: 'whyyou',
    area: 6,
    q: 'Why are you the person to build this?',
    hint: "Unfair advantages count: scars, access, obsession. Modesty doesn't help the analysis.",
    ph: "What you know or can reach that others can't…",
  },
] as const;

/** Set form, for validating a question id arriving from a client. */
export const INTERVIEW_QUESTION_IDS: ReadonlySet<string> = new Set(
  INTERVIEW_QUESTIONS.map((q) => q.id),
);

/**
 * Brief section assembly (the prototype's SECTION_MAP + sectionText()).
 *
 * NOT LLM-GENERATED. The launch screen's brief is deterministic string
 * assembly from the interview answers — free, instant, and it cannot
 * hallucinate the user's own idea back at them. Keep it that way unless
 * explicitly asked to change it (BACKLOG.md §0.3).
 */
export const BRIEF_SECTIONS = [
  { key: 'who', label: 'PROBLEM & WHO HAS IT', from: ['who'] },
  { key: 'whynow', label: 'WHY NOW', from: ['whynow'] },
  { key: 'insight', label: 'YOUR UNIQUE INSIGHT', from: ['insight'] },
  { key: 'alts', label: 'ALTERNATIVES & COMPETITION', from: ['alts'] },
  { key: 'edge', label: 'YOUR EDGE', from: ['edge'] },
  { key: 'model', label: 'BUSINESS MODEL', from: ['model'] },
  { key: 'whyyou', label: 'WHY YOU', from: ['whyyou'] },
] as const satisfies readonly {
  key: string;
  label: string;
  from: readonly InterviewQuestionId[];
}[];

/** Copy shown for a section the user skipped. */
export const SKIPPED_SECTION_TEXT =
  "Not answered — we'll research this ourselves and flag what we assume.";

export const SKIPPED_BADGE = 'SKIPPED · WE\'LL RESEARCH IT';
